import { createContext, useCallback, useContext } from 'react';
import useApiService from './ApiService';
import CONFIG from './CONFIG';
import {
  PromptAnalyzeApi,
  PromptApi,
  PromptApiList,
  PromptCreateApi,
  PromptIssue,
  PromptRewriteApi,
  PromptRewriteData,
} from './PromptApiProviderTypes';

interface PromptApiContextType {
  getPrompts: (skip?: number, limit?: number) => Promise<PromptApiList>;
  getPromptById: (promptId: string) => Promise<PromptApi>;
  createPrompt: (prompt: PromptCreateApi) => Promise<PromptApi>;
  updatePrompt: (promptId: string, prompt: PromptCreateApi) => Promise<PromptApi>;
  deletePrompt: (promptId: string) => Promise<void>;
  analyzePrompt: (prompt: string) => Promise<PromptAnalyzeApi>;
  rewritePrompt: (prompt: string, issues: PromptIssue[]) => Promise<PromptRewriteApi>;
}

const PromptApiContext = createContext<PromptApiContextType | null>(null);

export function PromptApiProvider({ children }: { children: React.ReactNode }) {
  const { get, put, del, post } = useApiService(CONFIG.SERVER_ASSISTANT);

  const getPrompts = useCallback(
    async (skip: number = 0, limit: number = 10): Promise<PromptApiList> => {
      try {
        const response = await get(`/v1/prompts?skip=${skip}&limit=${limit}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching prompts:', error);
        throw error;
      }
    },
    [get],
  );

  const getPromptById = useCallback(
    async (promptId: string): Promise<PromptApi> => {
      try {
        const response = await get(`/v1/prompts/${promptId}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching prompt by ID:', error);
        throw error;
      }
    },
    [get],
  );

  const createPrompt = useCallback(
    async (prompt: PromptCreateApi): Promise<PromptApi> => {
      try {
        const response = await put(`/v1/prompts`, prompt);
        return response.data;
      } catch (error) {
        console.error('Error creating prompt:', error);
        throw error;
      }
    },
    [put],
  );

  const updatePrompt = useCallback(
    async (promptId: string, prompt: PromptCreateApi): Promise<PromptApi> => {
      try {
        const response = await put(`/v1/prompts/${promptId}`, prompt);
        return response.data;
      } catch (error) {
        console.error('Error updating prompt:', error);
        throw error;
      }
    },
    [put],
  );

  const deletePrompt = useCallback(
    async (promptId: string): Promise<void> => {
      try {
        const response = await del(`/v1/prompts/${promptId}`);
        return response.data;
      } catch (error) {
        console.error('Error deleting prompt:', error);
        throw error;
      }
    },
    [del],
  );

  const analyzePrompt = useCallback(
    async (prompt: string): Promise<PromptAnalyzeApi> => {
      try {
        const response = await post(`/v1/prompts/analyze`, {
          prompt,
        });
        return response.data;
      } catch (error) {
        console.error('Error analyze prompt:', error);
        throw error;
      }
    },
    [post],
  );

  const rewritePrompt = useCallback(
    async (prompt: string, issues: PromptIssue[]): Promise<PromptRewriteApi> => {
      try {
        const response = await post(`/v1/prompts/rewrite`, {
          prompt,
          issues,
        });
        return response.data;
      } catch (error) {
        console.error('Error rewrite prompt:', error);
        throw error;
      }
    },
    [post],
  );

  return (
    <PromptApiContext.Provider
      value={{
        getPrompts,
        getPromptById,
        createPrompt,
        updatePrompt,
        deletePrompt,
        analyzePrompt,
        rewritePrompt,
      }}
    >
      {children}
    </PromptApiContext.Provider>
  );
}

export function usePromptApi(): PromptApiContextType {
  const context = useContext(PromptApiContext);
  if (!context) {
    throw new Error('usePromptApi must be used within a PromptApiProvider');
  }
  return context;
}
